import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Button,
  Space,
  Input,
  Select,
  Tooltip,
  Badge,
  Typography,
} from "antd";
import {
  PhoneOutlined,
  ClockCircleOutlined,
  UserOutlined,
  FilterOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import CallRequestCard from "../components/CallRequestCard";
import CallRequestDetailModal from "../components/CallRequestDetailModal";
import {
  getCallRequests,
  getCallRequestStats,
} from "../services/callRequestApiService";
import {
  logError,
  logInfo,
} from "@/shared/utils/logger.js";

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const CallQueuePage = () => {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: "active",
    assigned: "all",
    search: "",
  });

  // Real API integration
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    overdue: 0,
    assigned: 0,
  });

  // Modal state
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedCallRequest, setSelectedCallRequest] = useState(null);

  // Business hours configuration
  const businessHoursConfig = {
    start: 9, // 9 AM
    end: 17, // 5 PM
    days: [1, 2, 3, 4, 5], // Monday = 1, Sunday = 0
    timezone: "local", // Could be configurable for different regions
  };

  // Helper function to calculate working hours SLA
  const calculateWorkingHoursSLA = (slaDueAt) => {
    if (!slaDueAt) return { status: "no_sla", message: "No SLA defined" };

    const now = new Date();
    const dueDate = new Date(slaDueAt);

    // Check if current time is within business hours
    const isBusinessHours = () => {
      const day = now.getDay();
      const hour = now.getHours();
      return (
        businessHoursConfig.days.includes(day) &&
        hour >= businessHoursConfig.start &&
        hour < businessHoursConfig.end
      );
    };

    // Calculate working minutes until SLA due
    const calculateWorkingMinutes = () => {
      let workingMinutes = 0;
      let current = new Date(now);

      while (current < dueDate) {
        const day = current.getDay();
        const hour = current.getHours();

        // Only count business hours on business days
        if (businessHoursConfig.days.includes(day)) {
          if (hour < businessHoursConfig.start) {
            // Before business hours, count from start of business day
            workingMinutes +=
              (businessHoursConfig.end - businessHoursConfig.start) * 60;
          } else if (
            hour >= businessHoursConfig.start &&
            hour < businessHoursConfig.end
          ) {
            // During business hours
            workingMinutes += (businessHoursConfig.end - hour) * 60;
          }
          // After business hours, don't count
        }

        // Move to next day
        current.setDate(current.getDate() + 1);
        current.setHours(businessHoursConfig.start, 0, 0, 0);
      }

      return workingMinutes;
    };

    const workingMinutes = calculateWorkingMinutes();
    const isOverdue = workingMinutes <= 0;

    if (isOverdue) {
      return {
        status: "breached",
        message: "SLA Breached",
        color: "#ff4d4f",
        workingMinutes: 0,
        isBusinessHours: isBusinessHours(),
        displayText: "OVERDUE",
      };
    }

    // Convert to hours and minutes for display
    const hours = Math.floor(workingMinutes / 60);
    const minutes = workingMinutes % 60;

    let status, color;
    if (workingMinutes <= 60) {
      // 1 hour or less
      status = "critical";
      color = "#ff4d4f";
    } else if (workingMinutes <= 240) {
      // 4 hours or less
      status = "warning";
      color = "#fa8c16";
    } else {
      status = "normal";
      color = "#52c41a";
    }

    return {
      status,
      message: `${hours}h ${minutes}m remaining`,
      color,
      workingMinutes,
      isBusinessHours: isBusinessHours(),
      displayText: hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`,
    };
  };

  // Helper function to get agent name
  const getAgentName = (assignedToUserId, assignedToUser) => {
    if (!assignedToUserId) return null;

    if (assignedToUser) {
      if (assignedToUser.firstName && assignedToUser.lastName) {
        return `${assignedToUser.firstName} ${assignedToUser.lastName}`;
      } else if (assignedToUser.name) {
        return assignedToUser.name;
      } else if (assignedToUser.email) {
        return assignedToUser.email;
      }
    }

    return "Unknown Agent";
  };

  // Fetch call requests from API
  const fetchCallRequests = async () => {
    setLoading(true);
    try {
      const result = await getCallRequests(filters);

      if (result.success) {
        setQueue(result.data);
      } else {
        logError("CallQueuePage: failed to fetch call requests", result.error);
        setQueue([]);
      }
    } catch (error) {
      logError("CallQueuePage: error fetching call requests", error);
      setQueue([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics from API
  const fetchStats = async () => {
    try {
      const result = await getCallRequestStats();
      if (result.success) {
        setStats(result.data);
      } else {
        logError("CallQueuePage: failed to fetch stats", result.error);
        // Calculate stats from queue as fallback
        // Use backend stats when available, fallback to simple counts
        const overdueCount = queue.filter((c) => {
          if (!c.scheduledAt) return false;
          const slaInfo = calculateWorkingHoursSLA(c.scheduledAt);
          return slaInfo.status === "breached";
        }).length;

        setStats({
          total: queue.length,
          pending: queue.filter((c) =>
            ["requested", "assigned", "scheduled", "calling"].includes(c.status)
          ).length,
          overdue: stats.overdue || overdueCount, // Use backend calculated overdue count
          assigned: queue.filter((c) => c.assignedToUserId).length,
        });
      }
    } catch (error) {
      logError("CallQueuePage: error fetching stats", error);
    }
  };

  useEffect(() => {
    fetchCallRequests();
  }, [filters]);

  useEffect(() => {
    fetchStats();
  }, [queue]);

  const filteredQueue = queue
    .filter((call) => {
      // Handle status filtering
      if (filters.status === "active") {
        // Show all active/pending calls
        if (
          !["requested", "assigned", "scheduled", "calling"].includes(
            call.status
          )
        )
          return false;
      } else if (filters.status !== "all" && call.status !== filters.status) {
        return false;
      }

      // Handle assignment filtering
      if (filters.assigned === "assigned" && !call.assignedToUserId)
        return false;
      if (filters.assigned === "unassigned" && call.assignedToUserId)
        return false;

      // Handle search filtering
      if (
        filters.search &&
        !call.customerId.toLowerCase().includes(filters.search.toLowerCase())
      )
        return false;
      return true;
    })
    .sort((a, b) => {
      // Priority 1: SLA Status (breached first, then critical, warning, normal)
      const getSlaPriority = (call) => {
        if (!call.scheduledAt) return 0;
        const slaInfo = calculateWorkingHoursSLA(call.scheduledAt);
        const priorityMap = {
          breached: 4,
          critical: 3,
          warning: 2,
          normal: 1,
          no_sla: 0,
        };
        return priorityMap[slaInfo.status] || 0;
      };

      const slaPriorityA = getSlaPriority(a);
      const slaPriorityB = getSlaPriority(b);

      if (slaPriorityA !== slaPriorityB) {
        return slaPriorityB - slaPriorityA; // Higher priority first
      }

      // Priority 2: Status (requested first, then assigned, etc.)
      const statusPriority = {
        requested: 3,
        assigned: 2,
        calling: 1,
        completed: 0,
        no_answer: 0,
        reschedule: 0,
      };
      const statusA = statusPriority[a.status] || 0;
      const statusB = statusPriority[b.status] || 0;

      if (statusA !== statusB) {
        return statusB - statusA; // Higher priority first
      }

      // Priority 3: Unassigned calls first (need attention)
      if (a.assignedToUserId !== b.assignedToUserId) {
        return a.assignedToUserId ? 1 : -1; // Unassigned first
      }

      // Priority 4: By creation time (newest first)
      return (
        new Date(b.createdAt || b.requestedAt) -
        new Date(a.createdAt || a.requestedAt)
      );
    });

  // Stats are now managed by state and API calls

  const getStatusColor = (status) => {
    const colors = {
      requested: "blue",
      assigned: "orange",
      calling: "purple",
      completed: "green",
      no_answer: "red",
      reschedule: "geekblue",
    };
    return colors[status] || "default";
  };

  const columns = [
    {
      title: "Customer",
      dataIndex: "customerId",
      key: "customerId",
      render: (customerId) => (
        <Space>
          <UserOutlined />
          {customerId}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: (
        <Space>
          SLA
          <Tooltip
            title={
              <div>
                <div>
                  <strong>Call Scheduling SLA</strong>
                </div>
                <div>• Based on when the call is scheduled to happen</div>
                <div>• Only counts business hours (Mon-Fri, 9 AM - 5 PM)</div>
                <div>
                  • Weekends and after-hours don&apos;t count against SLA
                </div>
                <div>• Shows time remaining until scheduled call</div>
                <div>• 🟢 Normal: More than 4 hours until call</div>
                <div>• 🟡 Warning: Less than 4 hours until call</div>
                <div>• 🔴 Critical: Less than 1 hour until call</div>
                <div>• 🚨 Breached: Call time has passed</div>
              </div>
            }
            placement="top"
          >
            <InfoCircleOutlined
              style={{
                color: "#1890ff",
                cursor: "help",
                fontSize: "16px",
                marginLeft: "8px",
              }}
            />
          </Tooltip>
        </Space>
      ),
      dataIndex: "scheduledAt",
      key: "scheduledAt",
      render: (scheduledAt) => {
        const slaInfo = calculateWorkingHoursSLA(scheduledAt);
        return (
          <Tooltip
            title={`Call scheduled for: ${scheduledAt ? new Date(scheduledAt).toLocaleString() : "Not scheduled"}. SLA: ${slaInfo.message}. Working hours: ${slaInfo.isBusinessHours ? "Yes" : "No"}.`}
          >
            <Space>
              <ClockCircleOutlined style={{ color: slaInfo.color }} />
              <Text style={{ color: slaInfo.color }}>
                {slaInfo.displayText || slaInfo.message}
              </Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => (
        <Text type="secondary">
          {createdAt ? new Date(createdAt).toLocaleDateString() : "N/A"}
        </Text>
      ),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: "descend",
    },
    {
      title: "Assigned To",
      dataIndex: "assignedToUserId",
      key: "assignedToUserId",
      render: (assignedToUserId, record) =>
        assignedToUserId ? (
          <Space>
            <UserOutlined />
            {getAgentName(assignedToUserId, record.assignedToUser)}
          </Space>
        ) : (
          <Tag color="orange">Unassigned</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<PhoneOutlined />}
            onClick={() => handleCallNow(record)}
          >
            Call
          </Button>
          <Button size="small" onClick={() => handleViewDetails(record)}>
            View
          </Button>
        </Space>
      ),
    },
  ];

  const handleCallNow = (call) => {
    logInfo("CallQueuePage: call initiated", { callRequestId: call?.id });
    // Implement call functionality
  };

  const handleViewDetails = (call) => {
    setSelectedCallRequest(call);
    setDetailModalVisible(true);
  };

  const refresh = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: "24px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <div>
            <Typography.Title level={2} style={{ margin: 0 }}>
              Call Queue
            </Typography.Title>
            <Typography.Text type="secondary">
              Manage customer call requests and SLA compliance
            </Typography.Text>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchCallRequests();
              fetchStats();
            }}
            loading={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: "24px" }}>
        <Col span={6}>
          <Card>
            <Statistic
              title={
                <Space>
                  Total Calls
                  <Tooltip
                    title="Total number of call requests in the system"
                    placement="top"
                  >
                    <InfoCircleOutlined
                      style={{
                        color: "#1890ff",
                        cursor: "help",
                        fontSize: "14px",
                      }}
                    />
                  </Tooltip>
                </Space>
              }
              value={stats.total}
              prefix={<PhoneOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={
                <Space>
                  Active Calls
                  <Tooltip
                    title="Call requests that are requested, assigned, scheduled, or currently being called"
                    placement="top"
                  >
                    <InfoCircleOutlined
                      style={{
                        color: "#1890ff",
                        cursor: "help",
                        fontSize: "14px",
                      }}
                    />
                  </Tooltip>
                </Space>
              }
              value={stats.pending}
              valueStyle={{ color: "#fa8c16" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={
                <Space>
                  Overdue
                  <Tooltip
                    title="Call requests where the scheduled call time has passed (based on working hours: Mon-Fri, 9 AM - 5 PM)"
                    placement="top"
                  >
                    <InfoCircleOutlined
                      style={{
                        color: "#1890ff",
                        cursor: "help",
                        fontSize: "14px",
                      }}
                    />
                  </Tooltip>
                </Space>
              }
              value={stats.overdue}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={
                <Space>
                  Assigned
                  <Tooltip
                    title="Call requests that have been assigned to agents"
                    placement="top"
                  >
                    <InfoCircleOutlined
                      style={{
                        color: "#1890ff",
                        cursor: "help",
                        fontSize: "14px",
                      }}
                    />
                  </Tooltip>
                </Space>
              }
              value={stats.assigned}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: "24px" }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Search
              placeholder="Search customer ID"
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Status"
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              style={{ width: "100%" }}
            >
              <Option value="all">All Statuses</Option>
              <Option value="active">Active Calls</Option>
              <Option value="requested">Requested</Option>
              <Option value="assigned">Assigned</Option>
              <Option value="scheduled">Scheduled</Option>
              <Option value="calling">Calling</Option>
              <Option value="completed">Completed</Option>
              <Option value="no_answer">No Answer</Option>
              <Option value="reschedule">Reschedule</Option>
              <Option value="escalated">Escalated</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Assignment"
              value={filters.assigned}
              onChange={(value) => setFilters({ ...filters, assigned: value })}
              style={{ width: "100%" }}
            >
              <Option value="all">All</Option>
              <Option value="assigned">Assigned</Option>
              <Option value="unassigned">Unassigned</Option>
            </Select>
          </Col>
          <Col span={10}>
            <Space>
              <FilterOutlined />
              <Text type="secondary">
                Showing {filteredQueue.length} of {queue.length} calls
              </Text>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Call Queue Table */}
      <Card>
        <div style={{ marginBottom: "16px" }}>
          <Text type="secondary">
            📊 Table sorted by priority: SLA Breached → Critical → Warning →
            Normal → Status → Assignment → Creation Time
          </Text>
        </div>
        <Table
          dataSource={filteredQueue}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </Card>

      {/* Call Request Detail Modal */}
      <CallRequestDetailModal
        visible={detailModalVisible}
        callRequest={selectedCallRequest}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedCallRequest(null);
        }}
        onUpdate={() => {
          fetchCallRequests();
          fetchStats();
        }}
      />
    </div>
  );
};

export default CallQueuePage;
